import { useEffect, useRef } from "react";
import {
  Engine,
  Scene,
  ArcRotateCamera,
  Vector3,
  Color4,
  HemisphericLight,
  LoadAssetContainerAsync,
  type AssetContainer,
} from "@babylonjs/core";
import "@babylonjs/loaders/SPLAT";
import { useViewerStore } from "../state/viewerStore";
import { QUALITY_TO_RESOLUTION_SCALE, detectInitialQuality } from "../utils/deviceTier";

export function SplatViewer() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<Engine | null>(null);
  const sceneRef = useRef<Scene | null>(null);
  const containerRef = useRef<AssetContainer | null>(null);

  const sceneUrl = useViewerStore((s) => s.sceneUrl);
  const setLoading = useViewerStore((s) => s.setLoading);
  const setLoadProgress = useViewerStore((s) => s.setLoadProgress);
  const setLoadError = useViewerStore((s) => s.setLoadError);
  const setStats = useViewerStore((s) => s.setStats);
  const setQuality = useViewerStore((s) => s.setQuality);
  const quality = useViewerStore((s) => s.quality);

  // Boot engine + scene + camera once.
  useEffect(() => {
    if (!canvasRef.current) return;
    const initialQuality = detectInitialQuality();
    setQuality(initialQuality);

    const engine = new Engine(canvasRef.current, true, {
      preserveDrawingBuffer: false,
      stencil: false,
      antialias: false,
      adaptToDeviceRatio: true,
      powerPreference: "high-performance",
    });
    engine.setHardwareScalingLevel(1 / QUALITY_TO_RESOLUTION_SCALE[initialQuality]);

    const scene = new Scene(engine);
    scene.clearColor = new Color4(0.043, 0.043, 0.047, 1);
    scene.autoClear = true;

    const camera = new ArcRotateCamera(
      "cam",
      -Math.PI / 2,
      Math.PI / 2.4,
      4,
      Vector3.Zero(),
      scene,
    );
    camera.attachControl(canvasRef.current, true);
    camera.lowerRadiusLimit = 0.5;
    camera.upperRadiusLimit = 50;
    camera.wheelDeltaPercentage = 0.02;
    camera.pinchDeltaPercentage = 0.02;
    camera.useBouncingBehavior = false;
    camera.useAutoRotationBehavior = false;
    camera.angularSensibilityX = 600;
    camera.angularSensibilityY = 600;
    camera.minZ = 0.05;
    camera.maxZ = 1000;
    camera.fov = 1.0;

    new HemisphericLight("hemi", new Vector3(0, 1, 0), scene);

    const onResize = () => engine.resize();
    window.addEventListener("resize", onResize);

    let lastStats = performance.now();
    engine.runRenderLoop(() => {
      scene.render();
      const now = performance.now();
      if (now - lastStats > 250) {
        const fps = engine.getFps();
        const splatMesh = scene.meshes.find((m) => m.getClassName?.() === "GaussianSplattingMesh") as
          | (typeof scene.meshes[number] & { splatsCount?: number })
          | undefined;
        setStats({
          fps: Math.round(fps),
          drawnSplats: splatMesh?.splatsCount ?? 0,
          totalSplats: splatMesh?.splatsCount ?? 0,
        });
        lastStats = now;
      }
    });

    engineRef.current = engine;
    sceneRef.current = scene;

    return () => {
      window.removeEventListener("resize", onResize);
      containerRef.current?.dispose();
      scene.dispose();
      engine.dispose();
      engineRef.current = null;
      sceneRef.current = null;
      containerRef.current = null;
    };
    // boot once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-apply resolution scaling whenever quality changes.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.setHardwareScalingLevel(1 / QUALITY_TO_RESOLUTION_SCALE[quality]);
  }, [quality]);

  // Load splat whenever sceneUrl changes.
  useEffect(() => {
    const engine = engineRef.current;
    const scene = sceneRef.current;
    if (!engine || !scene || !sceneUrl) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadProgress(0);
      setLoadError(null);
      try {
        containerRef.current?.dispose();
        containerRef.current = null;

        const container = await LoadAssetContainerAsync(sceneUrl!, scene!, {
          onProgress: (ev) => {
            if (ev.lengthComputable && ev.total > 0) {
              setLoadProgress(ev.loaded / ev.total);
            }
          },
          pluginExtension: sceneUrl!.endsWith(".ply")
            ? ".ply"
            : sceneUrl!.endsWith(".splat")
            ? ".splat"
            : ".spz",
        });
        if (cancelled) {
          container.dispose();
          return;
        }
        container.addAllToScene();
        containerRef.current = container;
        setLoadProgress(1);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setLoadError(msg);
        setLoading(false);
        console.error("[splat-viewer] failed to load:", err);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [sceneUrl, setLoading, setLoadProgress, setLoadError]);

  return <canvas ref={canvasRef} style={{ width: "100vw", height: "100vh", display: "block" }} />;
}
